import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { useWallet } from '../context/WalletContext';
import { MINING_ADDRESS, TOKEN_ADDRESS } from '../config';
import miningAbi from '../abis/MiningContract.json';
import tokenAbi from '../abis/TokenContract.json';

export default function UserInfo() {
    const { account, provider } = useWallet();
    const [loading, setLoading] = useState(false);
    const [userInfo, setUserInfo] = useState(null);

    const formatDate = (timestamp) => {
        if (!timestamp || Number(timestamp) === 0) return '无';
        return new Date(Number(timestamp) * 1000).toLocaleString('zh-CN');
    };

    const formatNum = (val, unit = '') => {
        const num = parseFloat(val);
        const formatted = isNaN(num) ? '0.0000000' : num.toFixed(7);
        return unit ? `${formatted} ${unit}` : formatted;
    };

    const getLevelName = (level) => {
        const levels = {
            0: '普通用户',
            1: 'V1',
            2: 'V2',
            3: 'V3',
            4: 'V4',
            5: 'V5',
            6: 'V6'
        };
        return levels[level] || '普通用户';
    };

    const fetchUserInfo = async () => {
        if (!account || !provider) return;
        setLoading(true);
        try {
            const miningContract = new ethers.Contract(
                MINING_ADDRESS,
                miningAbi,
                provider
            );
            const tokenContract = new ethers.Contract(
                TOKEN_ADDRESS,
                tokenAbi,
                provider
            );

            const [
                basicInfo,
                bnbBalance,
                usdtBalance,
                claimableDividend,
                nextClaimTime,
                shareholderInfo,
                pendingRewards,
                pendingCertificates,
                pendingEmptyOrders
            ] = await Promise.all([
                miningContract.users(account),
                provider.getBalance(account),
                tokenContract.balanceOf(account).catch((error) => {
                    console.warn('读取代币余额失败，已回退为 0:', error?.message || error);
                    return 0n;
                }),
                miningContract.getClaimableDividend(account),
                miningContract.getNextClaimTime(account),
                miningContract.shareholders(account),
                miningContract.getPendingRewards(account),
                miningContract.pendingCertificates(account),
                miningContract.pendingEmptyOrders(account)
            ]);

            let specialOrderInfo = null;
            try {
                const specialOrder = await miningContract.specialOrders(account);

                if (specialOrder && Number(specialOrder.startTime) > 0) {
                    specialOrderInfo = {
                        certificateAmount: ethers.formatUnits(specialOrder.certificateAmount, 18),
                        totalAmount: ethers.formatUnits(specialOrder.certificateAmount, 18),
                        releasedAmount: ethers.formatUnits(specialOrder.totalReleased, 18),
                        startTime: specialOrder.startTime.toString(),
                        totalReleased: ethers.formatUnits(specialOrder.totalReleased, 18),
                        totalWithdrawn: ethers.formatUnits(specialOrder.totalWithdrawn, 18)
                    };
                }
            } catch (err) {
                console.log('无锁仓订单信息');
            }

            setUserInfo({
                bnbBalance: ethers.formatEther(bnbBalance),
                usdtBalance: ethers.formatUnits(usdtBalance, 18),
                totalDeposit: ethers.formatEther(basicInfo.totalDeposit),
                teamSize: basicInfo.teamSize.toString(),
                teamPerformance: ethers.formatUnits(basicInfo.teamTotalPerformance, 18),
                maxTeamPerformance: ethers.formatUnits(basicInfo.teamMaxSubPerformance, 18),
                otherTeamPerformance: ethers.formatUnits(basicInfo.teamTotalPerformance - basicInfo.teamMaxSubPerformance, 18),
                lastMiningTime: basicInfo.lastRewardUpdate.toString(),
                level: basicInfo.manualLevel.toString(),
                realLevel: basicInfo.realLevel.toString(),
                maxLevel: basicInfo.maxLevel.toString(),
                levelExpireTime: basicInfo.levelExpireTime.toString(),
                isRegistered: basicInfo.isRegistered,
                hasPendingCertificates: pendingCertificates > 0n,
                hasPendingEmptyOrders: pendingEmptyOrders > 0n,
                pendingCertificatesAmount: ethers.formatUnits(pendingCertificates, 18),
                pendingEmptyOrdersAmount: ethers.formatUnits(pendingEmptyOrders, 18),
                totalDepositUsdt: ethers.formatUnits(basicInfo.totalDepositUsdt, 18),
                isShareholder: shareholderInfo.isActive,
                orderAmount: ethers.formatUnits(shareholderInfo.orderAmount, 18),
                totalClaimed: ethers.formatEther(shareholderInfo.totalClaimed),
                totalWithdrawn: ethers.formatUnits(basicInfo.totalWithdrawn, 18),
                claimableDividend: ethers.formatUnits(claimableDividend, 18),
                nextClaimTime: nextClaimTime.toString(),
                specialOrder: specialOrderInfo,
                normalReward: ethers.formatUnits(pendingRewards[0], 18),
                specialReward: ethers.formatUnits(pendingRewards[1], 18)
            });
        } catch (err) {
            console.error('获取用户信息失败:', err);
            toast.error('获取用户信息失败: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (account) {
            fetchUserInfo();
        }
    }, [account]);

    if (!account) {
        return (
            <div className="container">
                <div className="card" style={ { textAlign: 'center', padding: '40px' } }>
                    <h2>请先连接钱包</h2>
                    <p>连接钱包后查看您的用户信息</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container">
                <div className="card" style={ { textAlign: 'center', padding: '40px' } }>
                    <div className="loading"></div>
                    <p>加载中...</p>
                </div>
            </div>
        );
    }

    if (!userInfo) {
        return (
            <div className="container">
                <div className="card" style={ { textAlign: 'center', padding: '40px' } }>
                    <p>暂无数据</p>
                    <button className="action-btn" onClick={ fetchUserInfo }>刷新</button>
                </div>
            </div>
        );
    }

    const isLevelInExpiry = Number(userInfo.levelExpireTime) > Math.floor(Date.now() / 1000);
    const realLevelNum = Number(userInfo.realLevel) || 0;
    const manualLevelNum = Number(userInfo.level) || 0;
    const displayLevel = isLevelInExpiry ? Math.max(realLevelNum, manualLevelNum) : realLevelNum;

    return (
        <div className="container">
            <div className="card">
                <h2 className="card-title">👤 基础信息</h2>
                <div className="info-row">
                    <span className="info-label">地址</span>
                    <span className="info-value">{ account }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">等级</span>
                    <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                        { getLevelName(displayLevel) }
                    </span>
                </div>
                <div className="info-row">
                    <span className="info-label">注册状态</span>
                    <span className="info-value">{ userInfo.isRegistered ? '已注册' : '未注册' }</span>
                </div>
                { userInfo.hasPendingCertificates && (
                    <div className="info-row">
                        <span className="info-label">未领取锁仓订单</span>
                        <span className="info-value">
                            有（{ formatNum(userInfo.pendingCertificatesAmount, 'USD') }）
                        </span>
                    </div>
                ) }
                { userInfo.hasPendingEmptyOrders && (
                    <div className="info-row">
                        <span className="info-label">未领取订单</span>
                        <span className="info-value">
                            有（{ formatNum(userInfo.pendingEmptyOrdersAmount, 'USD') }）
                        </span>
                    </div>
                ) }
            </div>

            <div className="card">
                <h2 className="card-title">额度与收益</h2>
                <div className="info-row">
                    <span className="info-label">总质押 (BNB)</span>
                    <span className="info-value">{ formatNum(userInfo.totalDeposit, 'BNB') }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">总质押价值 (USD)</span>
                    <span className="info-value">{ formatNum(userInfo.totalDepositUsdt, 'USD') }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">总额度</span>
                    <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                        { formatNum(parseFloat(userInfo.totalDepositUsdt) * 6, 'USD') }
                    </span>
                </div>
                <div className="info-row">
                    <span className="info-label">剩余额度</span>
                    <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                        { formatNum(parseFloat(userInfo.totalDepositUsdt) * 6 - parseFloat(userInfo.totalWithdrawn), 'USD') }
                    </span>
                </div>
            </div>

            <div className="card">
                <h2 className="card-title">待提取奖励</h2>
                <div className="info-row">
                    <span className="info-label">普通质押奖励 (HP)</span>
                    <span className="info-value">{ formatNum(userInfo.normalReward, 'HP') }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">线性释放奖励 (HP)</span>
                    <span className="info-value">{ formatNum(userInfo.specialReward, 'HP') }</span>
                </div>
                { (userInfo.isShareholder || userInfo.specialOrder) && (
                    <div className="info-row">
                        <span className="info-label">待提取分红 (BNB)</span>
                        <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                            { formatNum(userInfo.claimableDividend, 'BNB') }
                        </span>
                    </div>
                ) }
            </div>

            <div className="card">
                <h2 className="card-title">团队信息</h2>
                <div className="info-row">
                    <span className="info-label">团队人数</span>
                    <span className="info-value">{ userInfo.teamSize } 人</span>
                </div>
                <div className="info-row">
                    <span className="info-label">团队业绩</span>
                    <span className="info-value">{ formatNum(userInfo.teamPerformance, 'BNB') }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">大区业绩</span>
                    <span className="info-value">{ formatNum(userInfo.maxTeamPerformance, 'BNB') }</span>
                </div>
                <div className="info-row">
                    <span className="info-label">小区业绩</span>
                    <span className="info-value">{ formatNum(userInfo.otherTeamPerformance, 'BNB') }</span>
                </div>
            </div>

            { userInfo.specialOrder && (
                <div className="card">
                    <h2 className="card-title">线性释放</h2>
                    <>
                        <div className="info-row">
                            <span className="info-label">锁仓订单金额</span>
                            <span className="info-value">{ formatNum(userInfo.specialOrder.certificateAmount, 'USD') }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">累计线性释放总量</span>
                            <span className="info-value">{ formatNum(userInfo.specialOrder.totalReleased, 'HP') }</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">累计产生收益(包含未解锁)</span>
                            <span className="info-value">{ formatNum(userInfo.specialOrder.totalWithdrawn, 'USD') }</span>
                        </div>
                    </>
                </div>
            ) }
            { (userInfo.isShareholder) && (
                <div className="card">
                    <h2 className="card-title">股东与分红</h2>

                    <div className="info-row">
                        <span className="info-label">股东金额权重</span>
                        <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                            { formatNum(userInfo.orderAmount, 'USD') }
                        </span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">可提取分红</span>
                        <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                            { formatNum(userInfo.claimableDividend, 'BNB') }
                        </span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">累计领取分红</span>
                        <span className="info-value">{ formatNum(userInfo.totalClaimed, 'BNB') }</span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">下次提取时间</span>
                        <span className="info-value">{ formatDate(userInfo.nextClaimTime) }</span>
                    </div>

                    { userInfo.isShareholder && (
                        <>
                            <div className="divider"></div>
                            <div className="info-row">
                                <span className="info-label">股东分红资格</span>
                                <span className="info-value" style={ { fontSize: '18px', fontWeight: '700', color: '#9acd32' } }>
                                    已激活
                                </span>
                            </div>
                        </>
                    ) }
                </div>
            ) }

            <button className="action-btn" onClick={ fetchUserInfo } style={ { marginTop: '20px', width: '100%' } }>
                刷新数据
            </button>
        </div>
    );
}
